import React, { useMemo, useState } from 'react';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { matchPlayerName, matchValue, playerMetricColumns, playerMetricValue, statisticNumber } from '../../utils/matchRecord';
import { isGoalkeeper } from '../../utils/playerDiscovery';
import PlayerRoleBadge from '../playerStatistics/PlayerRoleBadge';

export default function MatchPlayerStatistics({ acta, partido, onPlayer }) {
  const { t, i18n } = useTranslation('matches');
  const theme = useTheme();
  const statistics = acta.estadisticasJugadores;
  const columns = playerMetricColumns(statistics);
  const catalog = statistics?.catalogoMetricas || statistics || {};
  const groups = (catalog.gruposMetricas || []).filter(group => group.types?.some(type => columns.some(column => column.id === type.id)));
  const [group, setGroup] = useState(groups[0]?.id || 'all');
  const [team, setTeam] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'rating', direction: 'descending' });
  const activeGroup = groups.find(item => item.id === group);
  const visibleColumns = (activeGroup ? activeGroup.types.map(type => columns.find(column => column.id === type.id)).filter(Boolean) : columns)
    .filter(column => column.id !== 'FS_RATING');
  const rows = useMemo(() => (statistics?.jugadores || []).map(player => {
    const roster = [...(acta.jugadoresLocales || []), ...(acta.jugadoresVisitantes || [])].find(item =>
      (player.codJugador != null && item.codJugador != null && String(item.codJugador) === String(player.codJugador))
      || (player.idFuente != null && item.idFlashscore != null && String(item.idFlashscore) === String(player.idFuente)));
    const codEquipo = player.codEquipo ?? roster?.codEquipo;
    return {
      ...roster, ...player, codEquipo,
      name: matchPlayerName(roster?.nombre || player.nombre) || t('record.unknownPlayer'),
      fullName: matchPlayerName(player.nombre || roster?.nombre) || t('record.unknownPlayer'),
    };
  }), [statistics, acta.jugadoresLocales, acta.jugadoresVisitantes, t]);
  const filtered = rows.filter(player => (team === 'all' || String(player.codEquipo) === team)
    && `${player.name} ${player.fullName}`.toLocaleLowerCase(i18n.language).includes(query.trim().toLocaleLowerCase(i18n.language)));
  const rating = player => player.estadisticas?.FS_RATING?.valor ?? player.valoracion;
  filtered.sort((a, b) => {
    if (sort.key === 'name') return a.name.localeCompare(b.name, i18n.language) * (sort.direction === 'ascending' ? 1 : -1);
    const get = player => statisticNumber(sort.key === 'rating' ? rating(player) : player.estadisticas?.[sort.key]?.valor);
    const first = get(a), second = get(b);
    if (first === null || second === null) return first === second ? 0 : first === null ? 1 : -1;
    return (first - second) * (sort.direction === 'ascending' ? 1 : -1);
  });
  const changeSort = key => setSort(current => ({
    key, direction: current.key === key ? (current.direction === 'descending' ? 'ascending' : 'descending')
      : columns.find(column => column.id === key)?.sort === 'ASC' ? 'ascending' : 'descending',
  }));
  const header = (key, label, description) => <th key={key} scope="col" aria-sort={sort.key === key ? sort.direction : 'none'}>
    <button type="button" title={description || label} onClick={() => changeSort(key)}>{label}
      {sort.key === key && <span aria-hidden="true">{sort.direction === 'ascending' ? ' ↑' : ' ↓'}</span>}</button></th>;
  return <section className="mr-panel mr-player-statistics" aria-label={t('record.playerStatistics')}>
    <header className="mr-section-heading"><div><span className="mr-eyebrow">{t('record.individualEyebrow')}</span>
      <h2>{t('record.playerStatistics')}</h2><p>{t('record.individualHelp')}</p></div></header>
    {statistics?.coberturaParcial === true && <p className="mr-help" role="note">{t('record.partialPlayerCoverage')}</p>}
    {acta.cerradaConIncidencias === true && <p className="mr-help" role="note">{t('record.closedPartial')}</p>}
    <div className="mr-segmented mr-player-groups" role="group" aria-label={t('record.category')}>
      {groups.map(item => <button type="button" key={item.id} aria-pressed={activeGroup?.id === item.id}
        onClick={() => setGroup(item.id)}>{item.label || item.id}</button>)}
      <button type="button" aria-pressed={!activeGroup} onClick={() => setGroup('all')}>{t('record.allMetrics')}</button>
    </div>
    <div className="mr-player-filters">
      <label>{t('record.searchPlayer')}<input type="search" value={query} onChange={event => setQuery(event.target.value)} /></label>
      <label>{t('record.team')}<select value={team} onChange={event => setTeam(event.target.value)}>
        <option value="all">{t('record.bothTeams')}</option>
        {partido.codEquipoLocal != null && <option value={partido.codEquipoLocal}>{partido.equipoLocal}</option>}
        {partido.codEquipoVisitante != null && <option value={partido.codEquipoVisitante}>{partido.equipoVisitante}</option>}
      </select></label>
    </div>
    <p className="mr-help">{t('record.playerCount', { count: filtered.length })}</p>
    {!catalog.tiposMetricas?.length && <p className="mr-help">{t('record.catalogUnavailable')}</p>}
    <div className="mr-player-table" tabIndex={0} role="region" aria-label={t('record.playerTable')}>
      <table aria-label={t('record.playerStatistics')}><thead><tr>{header('name', t('record.player'))}
        {header('rating', t('record.rating'))}
        {visibleColumns.map(column => header(column.id, column.label, column.description))}</tr></thead>
        <tbody>{filtered.map((player, index) => {
          const home = player.codEquipo != null && partido.codEquipoLocal != null && String(player.codEquipo) === String(partido.codEquipoLocal);
          const away = player.codEquipo != null && partido.codEquipoVisitante != null && String(player.codEquipo) === String(partido.codEquipoVisitante);
          const name = home ? partido.equipoLocal : away ? partido.equipoVisitante : '—';
          const shield = home ? partido.escudoEquipoLocal : away ? partido.escudoEquipoVisitante : null;
          const position = typeof player.posicion === 'object' ? player.posicion?.name || player.posicion?.nombre : player.posicion;
          const score = matchValue(rating(player));
          return <tr key={player.codJugador || player.idFuente || index}
            className={isGoalkeeper(player) ? 'player-goalkeeper' : undefined} data-theme={theme.palette.mode}>
            <th scope="row"><div className="mr-table-player">
              <span className="mr-table-avatar"><PersonOutlineIcon aria-hidden="true" />
                {player.foto && <img src={player.foto} alt="" loading="lazy" onError={event => { event.currentTarget.hidden = true; }} />}
                {shield && <img className="mr-table-shield" src={shield} alt="" loading="lazy"
                  onError={event => { event.currentTarget.hidden = true; }} />}</span>
              <div><button type="button" className="mr-player-link" title={player.fullName}
                onClick={() => player.codJugador && onPlayer(player.codJugador)}>
                  {player.numero != null && player.numero !== '' && <span className="mr-roster-number">{player.numero}</span>}
                  {player.name}</button><PlayerRoleBadge player={player} />
                <small>{position || t(player.titular ? 'record.starter' : 'record.squadPlayer')}</small>
                <small className="mr-table-team">{name}</small></div>
            </div></th>
            <td className="mr-rating-cell"><span className={score !== '—' ? 'mr-rating-badge' : undefined}>{score}</span></td>
            {visibleColumns.map(column => <td key={column.id}>{playerMetricValue(player, column)}</td>)}
          </tr>;
        })}</tbody>
      </table>
    </div>
    {!filtered.length && <p className="mr-empty">{t('record.noPlayers')}</p>}
  </section>;
}
